import { useState, useMemo, useCallback } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { useAuth } from "@/hooks/use-auth";
import { Link, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  FileText,
  Plus,
  Trash2,
  Loader2,
  Calendar,
  ClipboardList,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Search,
  Eye,
  Pencil,
  Send,
} from "lucide-react";
import type {
  MeetingMinutesRecord,
  MeetingMinutesActionItem,
  MeetingMinutesListResponse,
  MeetingMinutesDetailResponse,
  LanesResponse,
  TeamMembersResponse,
  ReferenceListsResponse,
  ReferenceListItemRecord,
} from "@shared/v2/types";

const MEETING_TYPES = [
  { value: "BOARD_MEETING", label: "Board Meeting" },
  { value: "EXCOM", label: "ExCom" },
  { value: "COMMITTEE", label: "Committee" },
  { value: "IC_MEETING", label: "Investment Committee" },
  { value: "SHAREHOLDER_MEETING", label: "Shareholder Meeting" },
  { value: "OTHER", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: "P1", label: "P1 (Urgent)" },
  { value: "P2", label: "P2 (High)" },
  { value: "P3", label: "P3 (Normal)" },
  { value: "P4", label: "P4 (Low)" },
];

function meetingTypeLabelStatic(type: string): string {
  return MEETING_TYPES.find((t) => t.value === type)?.label || type;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const EMPTY_ACTION_ITEM: MeetingMinutesActionItem = { title: "", authority_lane: "", assigned_to: "", due_date: "", priority: "P3", signoff_required: true, instruction: "", doc_type: "" };

interface FormState {
  reference_number: string;
  meeting_name: string;
  meeting_type: string;
  meeting_date: string;
  minutes_prepared_by: string;
  action_items: MeetingMinutesActionItem[];
}

const INITIAL_FORM: FormState = {
  reference_number: "",
  meeting_name: "",
  meeting_type: "",
  meeting_date: "",
  minutes_prepared_by: "",
  action_items: [],
};

interface ActiveUser {
  id: string;
  username: string;
  name: string;
  email: string | null;
}

const LANE_LABELS: Record<string, string> = {
  EDGE_CHAIRMAN: "EDGE Chairman",
  EDGE_MD_OFFICE: "EDGE MD Office",
  BOARD_EXCOM: "Board / ExCom",
  SDF_MD: "SDF MD",
};

function NotAuthorizedToRegisterDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1b3e] border-white/10 text-white" data-testid="dialog-not-authorized-meeting-minutes">
        <DialogHeader>
          <DialogTitle>Not authorized to register tasks</DialogTitle>
          <DialogDescription className="text-white/70 pt-2">
            You are not authorized to register tasks. Please request permission from your Chief.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onClose} data-testid="button-not-authorized-back">Back to Updates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MeetingMinutesPage() {
  const { containerClass } = useLayout();
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { user, canRegisterTasks, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ ...INITIAL_FORM });
  const [searchText, setSearchText] = useState("");
  const [autoRefGenerated, setAutoRefGenerated] = useState(false);
  const [isGeneratingRef, setIsGeneratingRef] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    details: true, actions: true,
  });

  const minutesQuery = useQuery<MeetingMinutesListResponse>({
    queryKey: ["/api/meeting-minutes"],
  });

  const lanesQuery = useQuery<LanesResponse>({
    queryKey: ["/api/governance/lanes"],
  });

  const teamQuery = useQuery<TeamMembersResponse>({
    queryKey: ["/api/team-members"],
  });

  const refListsQuery = useQuery<ReferenceListsResponse>({
    queryKey: ["/api/governance/reference-lists"],
  });
  const activeUsersQuery = useQuery<{ data: ActiveUser[] }>({
    queryKey: ["/api/users/active"],
  });
  const activeUsers = activeUsersQuery.data?.data ?? [];
  const refLists = refListsQuery.data?.data ?? {};
  const dynamicMeetingTypes: ReferenceListItemRecord[] = (refLists["MEETING_TYPE"] ?? []).filter((m) => m.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const docTypes: ReferenceListItemRecord[] = (refLists["DOC_TYPE"] ?? []).filter((d) => d.is_active).sort((a, b) => a.sort_order - b.sort_order);

  const meetingTypeLabel = (type: string): string => {
    const dynamic = dynamicMeetingTypes.find((m) => m.item_key === type);
    if (dynamic) return dynamic.label;
    return meetingTypeLabelStatic(type);
  };

  const viewQuery = useQuery<MeetingMinutesDetailResponse>({
    queryKey: ["/api/meeting-minutes", viewingId],
    enabled: !!viewingId,
  });

  const allMinutes = minutesQuery.data?.data ?? [];
  const lanes = lanesQuery.data?.data ?? [];
  const teamMembers = (teamQuery.data?.data ?? []).filter((m) => m.is_active);

  const filteredMinutes = useMemo(() => {
    if (!searchText.trim()) return allMinutes;
    const q = searchText.toLowerCase();
    return allMinutes.filter(
      (m) =>
        m.reference_number.toLowerCase().includes(q) ||
        meetingTypeLabel(m.meeting_type).toLowerCase().includes(q)
    );
  }, [allMinutes, searchText]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = () => ({
    ...form,
    time_from: "",
    time_to: "",
    location: "",
    chaired_by: "",
    quorum_confirmed: false,
    attendees: [],
    agenda_items: [],
    decisions: [],
    next_meeting_date: "",
    next_meeting_time: "",
    next_meeting_location: "",
    prepared_by_name: "",
    prepared_by_title: "",
    prepared_by_date: "",
    approved_by_name: "",
    approved_by_title: "",
    approved_by_date: "",
    status: "finalized" as const,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (editingId) {
        const res = await apiRequest("PATCH", `/api/meeting-minutes/${editingId}`, payload);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/meeting-minutes", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-minutes"] });
      if (editingId) {
        queryClient.invalidateQueries({ queryKey: ["/api/meeting-minutes", editingId] });
      }
      toast({ title: editingId ? "Meeting tasks updated" : "Meeting tasks saved" });
      setShowForm(false);
      setEditingId(null);
      setAutoRefGenerated(false);
      setForm({ ...INITIAL_FORM });
    },
    onError: (err: any) => {
      showFromApiError("Save Failed", err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/meeting-minutes/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-minutes"] });
      toast({ title: "Meeting tasks deleted" });
    },
    onError: (err: any) => {
      showFromApiError("Delete Failed", err);
    },
  });

  const createActionItemsMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/meeting-minutes/${id}/create-action-items`);
      return res.json();
    },
    onSuccess: (data: any, id: string) => {
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-minutes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-minutes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: data.message || "Action items created" });
    },
    onError: (err: any) => {
      showFromApiError("Action Item Creation Failed", err);
    },
  });

  const openEdit = (m: MeetingMinutesRecord) => {
    setEditingId(m.id);
    setForm({
      reference_number: m.reference_number,
      meeting_name: m.meeting_name || "",
      meeting_type: m.meeting_type,
      meeting_date: m.meeting_date,
      minutes_prepared_by: m.minutes_prepared_by || "",
      action_items: m.action_items,
    });
    setAutoRefGenerated(false);
    setShowForm(true);
    setExpandedSections({ details: true, actions: true });
  };

  const addActionItem = () => setField("action_items", [...form.action_items, { ...EMPTY_ACTION_ITEM }]);
  const removeActionItem = (i: number) => setField("action_items", form.action_items.filter((_, idx) => idx !== i));
  const updateActionItem = (i: number, field: keyof MeetingMinutesActionItem, value: any) => {
    const arr = [...form.action_items];
    arr[i] = { ...arr[i], [field]: value };
    setField("action_items", arr);
  };

  const generateAutoRef = useCallback(async (meetingTypeKey: string, meetingDate: string) => {
    if (!meetingTypeKey || !meetingDate) {
      setAutoRefGenerated(false);
      return;
    }
    const mtItem = dynamicMeetingTypes.find((m) => m.item_key === meetingTypeKey);
    if (!mtItem?.ref_code) {
      setAutoRefGenerated(false);
      return;
    }

    setIsGeneratingRef(true);
    try {
      const res = await apiRequest("POST", "/api/ref-sequence/next", {
        meetingTypeCode: mtItem.ref_code,
        meetingDate,
      });
      const data = await res.json();
      if (data?.data?.refNumber) {
        setForm((prev) => ({ ...prev, reference_number: data.data.refNumber }));
        setAutoRefGenerated(true);
      }
    } catch {
      setAutoRefGenerated(false);
    } finally {
      setIsGeneratingRef(false);
    }
  }, [dynamicMeetingTypes]);

  const handleMeetingTypeChange = (val: string) => {
    setField("meeting_type", val);
    if (form.meeting_date) {
      generateAutoRef(val, form.meeting_date);
    }
  };

  const handleMeetingDateChange = (val: string) => {
    setField("meeting_date", val);
    if (form.meeting_type) {
      generateAutoRef(form.meeting_type, val);
    }
  };

  const canSave = form.reference_number.trim() && form.meeting_name.trim() && form.meeting_type && form.meeting_date;

  const viewData = viewQuery.data?.data;

  if (minutesQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}>
        <Loader2 className="h-8 w-8 animate-spin text-[#00CFE8]" />
      </div>
    );
  }

  if (!authLoading && !canRegisterTasks) {
    return <NotAuthorizedToRegisterDialog onClose={() => setLocation("/updates")} />;
  }

  return (
    <div className="min-h-screen p-6" style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}>
      <div className={`${containerClass} mx-auto space-y-6`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-6 w-6 text-[#FF9F43]" />
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="text-page-title">Meeting Tasks</h1>
              <p className="text-white/50 text-sm">Log a meeting and bulk-register the action items that came out of it</p>
            </div>
          </div>
          <Button
            className="bg-[#FF9F43] hover:bg-[#FF9F43]/80 text-white"
            onClick={() => { setForm({ ...INITIAL_FORM }); setEditingId(null); setAutoRefGenerated(false); setShowForm(true); }}
            data-testid="button-new-minutes"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Meeting Tasks
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Search className="h-4 w-4 text-white/40" />
          <Input
            placeholder="Search by reference or meeting type..."
            className="bg-white/5 border-white/10 text-white max-w-md"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            data-testid="input-search-minutes"
          />
          <Badge variant="outline" className="text-white/50 border-white/15 text-xs">
            {filteredMinutes.length} record{filteredMinutes.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        {filteredMinutes.length === 0 ? (
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
            <CardContent className="p-12 text-center">
              <ClipboardList className="h-12 w-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50" data-testid="text-empty-minutes">
                {searchText ? "No matching meeting tasks found." : "No meeting tasks recorded yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredMinutes.map((m) => {
              const actionCount = m.action_items?.length || 0;
              const createdCount = m.action_items?.filter((a) => a.created_item_id).length || 0;
              return (
                <Card
                  key={m.id}
                  className="border-white/8 transition-colors hover-elevate cursor-pointer"
                  style={{ background: "rgba(255,255,255,0.04)" }}
                  data-testid={`card-minutes-${m.id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0 flex-1" onClick={() => setViewingId(m.id)}>
                      <FileText className="h-5 w-5 text-[#FF9F43] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white">{m.reference_number}</span>
                          <Badge variant="outline" className="text-xs text-[#FF9F43] border-[#FF9F43]/30">
                            {meetingTypeLabel(m.meeting_type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40">
                          <span>{formatDate(m.meeting_date)}</span>
                          {actionCount > 0 && (
                            <><span className="text-white/20">|</span>
                            <span className="text-[#00CFE8]">{createdCount}/{actionCount} actions registered</span></>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {actionCount > 0 && createdCount < actionCount && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#28C76F] border-[#28C76F]/30 hover:bg-[#28C76F]/10 text-xs"
                          disabled={createActionItemsMutation.isPending}
                          onClick={(e) => { e.stopPropagation(); createActionItemsMutation.mutate(m.id); }}
                          data-testid={`button-create-items-${m.id}`}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Register Tasks
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/40 hover:text-white"
                        onClick={() => setViewingId(m.id)}
                        data-testid={`button-view-${m.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/40 hover:text-[#FF9F43]"
                        onClick={(e) => { e.stopPropagation(); openEdit(m); }}
                        data-testid={`button-edit-${m.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/40 hover:text-red-400"
                        onClick={(e) => { e.stopPropagation(); if (confirm("Delete these meeting tasks?")) deleteMutation.mutate(m.id); }}
                        data-testid={`button-delete-${m.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={!!viewingId} onOpenChange={(open) => { if (!open) setViewingId(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#FF9F43]" />
              {viewData?.reference_number || "Meeting Tasks"}
            </DialogTitle>
          </DialogHeader>
          {viewQuery.isLoading ? (
            <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-[#00CFE8]" /></div>
          ) : viewData ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-white/40">Meeting Type:</span> <span className="text-white ml-2">{meetingTypeLabel(viewData.meeting_type)}</span></div>
                <div><span className="text-white/40">Date:</span> <span className="text-white ml-2">{formatDate(viewData.meeting_date)}</span></div>
                {viewData.meeting_name && <div><span className="text-white/40">Meeting:</span> <span className="text-white ml-2">{viewData.meeting_name}</span></div>}
                {viewData.minutes_prepared_by && <div><span className="text-white/40">Logged By:</span> <span className="text-white ml-2">{viewData.minutes_prepared_by}</span></div>}
                {viewData.sent_by && viewData.sent_by !== viewData.minutes_prepared_by && <div><span className="text-white/40">Sent By:</span> <span className="text-white ml-2">{viewData.sent_by}</span></div>}
              </div>

              {viewData.action_items.length > 0 && (
                <>
                  <Separator className="bg-white/8" />
                  <div>
                    <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2"><ClipboardList className="h-4 w-4" />Action Items ({viewData.action_items.length})</h3>
                    <div className="space-y-2">
                      {viewData.action_items.map((a, i) => (
                        <Card key={i} className="border-white/8" style={{ background: "rgba(255,255,255,0.03)" }}>
                          <CardContent className="p-3 text-sm">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">{a.title}</span>
                                <Badge variant="outline" className="text-xs text-white/50 border-white/20">{a.priority}</Badge>
                                {a.created_item_id ? (
                                  <Link href={`/action-items/${a.created_item_id}`}>
                                    <Badge variant="outline" className="text-xs text-[#28C76F] border-[#28C76F]/30 cursor-pointer hover:bg-[#28C76F]/10">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />Registered
                                    </Badge>
                                  </Link>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-yellow-400 border-yellow-400/30">Pending</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-white/40 flex-wrap">
                              <span>{LANE_LABELS[a.authority_lane] || a.authority_lane}</span>
                              <span className="text-white/20">|</span>
                              <span>{a.assigned_to}</span>
                              <span className="text-white/20">|</span>
                              <span>Due: {formatDate(a.due_date)}</span>
                              {a.doc_type && (
                                <>
                                  <span className="text-white/20">|</span>
                                  <span>Doc: {docTypes.find((d) => d.item_key === a.doc_type)?.label || a.doc_type}</span>
                                </>
                              )}
                            </div>
                            {a.instruction && <div className="text-white/50 text-xs mt-1">{a.instruction}</div>}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditingId(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#FF9F43]" />
              {editingId ? "Edit Meeting Tasks" : "New Meeting Tasks"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Section 1: Meeting Details */}
            <SectionHeader title="1. Meeting Details" icon={<Calendar className="h-4 w-4" />} sectionKey="details" expanded={expandedSections.details} toggle={toggleSection} />
            {expandedSections.details && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 text-xs">Reference Number *</Label>
                  <div className="relative">
                    <Input
                      className={`bg-white/5 border-white/10 text-white text-sm mt-1 ${autoRefGenerated ? "font-mono text-[#28C76F] bg-[#28C76F]/5" : ""}`}
                      placeholder={dynamicMeetingTypes.length > 0 ? "Select type & date to auto-generate" : "e.g., BM/2026/003"}
                      value={form.reference_number}
                      onChange={(e) => { if (!autoRefGenerated) setField("reference_number", e.target.value); }}
                      readOnly={autoRefGenerated}
                      data-testid="input-ref-number"
                    />
                    {autoRefGenerated && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#28C76F] text-xs">auto</span>}
                    {isGeneratingRef && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-cyan-400 text-xs">generating...</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Meeting Type *</Label>
                  <Select value={form.meeting_type} onValueChange={handleMeetingTypeChange}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm mt-1" data-testid="select-meeting-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dynamicMeetingTypes.length > 0 ? dynamicMeetingTypes.map((t) => (<SelectItem key={t.item_key} value={t.item_key}>{t.label}</SelectItem>)) : MEETING_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Date *</Label>
                  <Input type="date" className="bg-white/5 border-white/10 text-white text-sm mt-1" value={form.meeting_date} onChange={(e) => handleMeetingDateChange(e.target.value)} data-testid="input-meeting-date" />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Logged By</Label>
                  <Select value={form.minutes_prepared_by} onValueChange={(v) => setField("minutes_prepared_by", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm mt-1" data-testid="select-logged-by">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeUsers.map((u) => (
                        <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-white/60 text-xs">Meeting Name *</Label>
                  <Input
                    className="bg-white/5 border-white/10 text-white text-sm mt-1"
                    placeholder="e.g. Q2 Board Meeting, Investment Committee, Audit Review"
                    value={form.meeting_name}
                    onChange={(e) => setField("meeting_name", e.target.value)}
                    data-testid="input-meeting-name"
                  />
                </div>
              </div>
            )}

            {/* Section 2: Action Items */}
            <SectionHeader title={`2. Action Items (${form.action_items.length})`} icon={<ClipboardList className="h-4 w-4" />} sectionKey="actions" expanded={expandedSections.actions} toggle={toggleSection} />
            {expandedSections.actions && (
              <div className="space-y-4">
                {form.action_items.length === 0 && (
                  <p className="text-white/30 text-xs text-center py-4">No action items added yet. Click below to add one.</p>
                )}
                {form.action_items.map((ai, i) => (
                  <Card key={i} className="border-[#00CFE8]/20" style={{ background: "rgba(0,207,232,0.03)" }}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs text-[#00CFE8] border-[#00CFE8]/30">Action #{i + 1}</Badge>
                        {ai.created_item_id ? (
                          <Badge variant="outline" className="text-xs text-[#28C76F] border-[#28C76F]/30"><CheckCircle2 className="h-3 w-3 mr-1" />Registered</Badge>
                        ) : (
                          <Button size="sm" variant="ghost" className="text-red-400/50 hover:text-red-400 p-1 h-auto" onClick={() => removeActionItem(i)}><Trash2 className="h-3 w-3" /></Button>
                        )}
                      </div>
                      <div>
                        <Label className="text-white/40 text-xs">Title / Description</Label>
                        <Input className="bg-white/5 border-white/10 text-white text-sm" value={ai.title} onChange={(e) => updateActionItem(i, "title", e.target.value)} disabled={!!ai.created_item_id} data-testid={`input-action-title-${i}`} />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-white/40 text-xs">Authority Lane</Label>
                          <Select value={ai.authority_lane} onValueChange={(v) => updateActionItem(i, "authority_lane", v)} disabled={!!ai.created_item_id}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="Select lane" /></SelectTrigger>
                            <SelectContent>
                              {lanes.map((l) => (<SelectItem key={l.lane_key} value={l.lane_key}>{l.display_name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white/40 text-xs">Assigned To</Label>
                          <Select value={ai.assigned_to} onValueChange={(v) => updateActionItem(i, "assigned_to", v)} disabled={!!ai.created_item_id}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="Select person" /></SelectTrigger>
                            <SelectContent>
                              {teamMembers.map((m) => (<SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white/40 text-xs">Due Date</Label>
                          <Input type="date" className="bg-white/5 border-white/10 text-white text-sm" value={ai.due_date} onChange={(e) => updateActionItem(i, "due_date", e.target.value)} disabled={!!ai.created_item_id} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-white/40 text-xs">Priority</Label>
                          <Select value={ai.priority} onValueChange={(v) => updateActionItem(i, "priority", v)} disabled={!!ai.created_item_id}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PRIORITY_OPTIONS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white/40 text-xs">Document Type</Label>
                          <Select
                            value={ai.doc_type || "__none__"}
                            onValueChange={(v) => updateActionItem(i, "doc_type", v === "__none__" ? "" : v)}
                            disabled={!!ai.created_item_id}
                          >
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm" data-testid={`select-action-doc-type-${i}`}>
                              <SelectValue placeholder="(none)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">(none)</SelectItem>
                              {docTypes.map((d) => (
                                <SelectItem key={d.item_key} value={d.item_key}>{d.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2 mt-5">
                          <Switch checked={ai.signoff_required} onCheckedChange={(v) => updateActionItem(i, "signoff_required", v)} disabled={!!ai.created_item_id} />
                          <Label className="text-white/40 text-xs">Sign-off Req.</Label>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/40 text-xs">Instruction / Notes</Label>
                        <Textarea className="bg-white/5 border-white/10 text-white text-sm min-h-[50px]" value={ai.instruction} onChange={(e) => updateActionItem(i, "instruction", e.target.value)} disabled={!!ai.created_item_id} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button size="sm" variant="outline" className="text-[#00CFE8] border-[#00CFE8]/30 text-xs" onClick={addActionItem} data-testid="button-add-action-item">
                  <Plus className="h-3 w-3 mr-1" />Add Action Item
                </Button>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" className="text-white/50" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            <Button
              className="bg-[#FF9F43] hover:bg-[#FF9F43]/80 text-white"
              disabled={!canSave || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              data-testid="button-save-minutes"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionHeader({ title, icon, sectionKey, expanded, toggle }: { title: string; icon: React.ReactNode; sectionKey: string; expanded: boolean; toggle: (key: string) => void }) {
  return (
    <button
      className="w-full flex items-center justify-between py-2 px-3 rounded-md bg-white/3 hover:bg-white/5 transition-colors"
      onClick={() => toggle(sectionKey)}
      type="button"
      data-testid={`section-toggle-${sectionKey}`}
    >
      <div className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-wider">
        {icon}
        {title}
      </div>
      {expanded ? <ChevronUp className="h-4 w-4 text-white/30" /> : <ChevronDown className="h-4 w-4 text-white/30" />}
    </button>
  );
}
